import React, { useEffect, useMemo } from 'react';
import { EmptyState, Flex, Spinner } from 'core/components';
import type Highcharts from 'highcharts';
import { observer } from 'mobx-react';
import { merge } from 'lodash';
import UdeLegend from 'app/views/boards/panels/ude/UdeLegend';
import { HighchartsVizTypes, TimeSeriesVizTypes } from 'app/views/ude/dataviews/util/highcharts';
import { Measurement, VizType } from '@kentik/ui-shared/query/query.types';
import storeLoader, { InjectedStoreProps } from 'app/stores/storeLoader';
import { DictionaryMeasurementsState, useDictionaryMeasurement } from 'app/stores/dictionary/useDictionaryMeasurements';
import { dataViewTypes } from 'app/stores/$dataviews';
import { getEnumHealthyValue, isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import type { AddRowFilters } from 'app/views/boards/panels/ude/rowFilters';
import HighchartsDataView from './dataviews/HighchartsDataView';
import UdeDataViewSkeleton from './UdeDataViewSkeleton';
import QueryModel from '../../stores/ude/QueryModel';
import { useDragToZoom } from './useDragToZoom';
import { ZoomSelectionPopover } from './components/ZoomSelectionPopover';
import { ZoomSelectionOverlay } from './components/ZoomSelectionOverlay';

const noop = () => {};

function getPlaceholderProps(queryModel: QueryModel, measurementState: DictionaryMeasurementsState) {
  if (queryModel.hasError || measurementState.isError) {
    return { title: 'There was an error running this query.', icon: 'error' };
  }
  return { title: 'No results found.', icon: 'issue' };
}

interface ChartProps {
  queryModel: QueryModel;
  measurement: Measurement;
  onChartRef?: (chart: Highcharts.Chart | null) => void;
  chartOverrides?: Highcharts.Options;
}

function Chart({ queryModel, measurement, onChartRef, chartOverrides }: ChartProps) {
  const chartType = queryModel.settings.chart.type;

  // TODO: Implement loading/error state for measurement fetch (may come for free with React-Query)
  if (!measurement) {
    return <Spinner />;
  }

  if (chartType && HighchartsVizTypes.includes(chartType)) {
    return (
      <HighchartsDataView
        measurement={measurement}
        queryModel={queryModel}
        onChartRef={onChartRef}
        chartOverrides={chartOverrides}
      />
    );
  }

  // TODO other chart types
  return <>Coming Soon!</>;
}

export interface UdeDataViewProps extends InjectedStoreProps<['$metrics']> {
  queryModel: QueryModel;
  /** Optional callback to receive the Highcharts chart instance (for cross-chart sync). */
  onChartRef?: (chart: Highcharts.Chart | null) => void;
  /** When set, overrides the default max-width for a right-positioned legend. */
  legendMaxWidth?: number | string;
  /** Arbitrary Highcharts options deep-merged into the generated chart config. */
  chartOverrides?: Highcharts.Options;
  /** Padding around the chart container. Defaults to 1 (theme spacing unit). */
  padding?: number;
  /** Called when user confirms "Zoom to Selection" — receives (startEpochSeconds, endEpochSeconds) */
  onZoomToSelection?: (start: number, end: number) => void;
  /** Enables row's include/exclude menu. */
  onAddRowFilters?: AddRowFilters;
  /** Query is waiting to run; shows the loading skeleton. */
  isPending?: boolean;
  /** Replaces the default "No results found." placeholder. Error and incompatibility placeholders still apply. */
  emptyState?: React.ReactNode;
}

function UdeDataView({
  queryModel,
  onChartRef,
  legendMaxWidth,
  chartOverrides,
  padding,
  onZoomToSelection,
  onAddRowFilters,
  isPending = false,
  emptyState
}: UdeDataViewProps) {
  const { measurement, ...measurementState } = useDictionaryMeasurement(queryModel.query.measurement);

  const isTimeSeries = TimeSeriesVizTypes.includes(queryModel.settings.chart.type as VizType);
  const {
    chartOverrides: zoomOverrides,
    selection,
    containerRef,
    confirmZoom,
    dismissSelection
  } = useDragToZoom({
    enabled: isTimeSeries && !!onZoomToSelection,
    onZoom: onZoomToSelection ?? noop
  });

  // Merge zoom overrides with any externally supplied chartOverrides
  const mergedOverrides = useMemo(() => {
    if (!zoomOverrides && !chartOverrides) {
      return undefined;
    }
    if (!zoomOverrides) {
      return chartOverrides;
    }
    if (!chartOverrides) {
      return zoomOverrides;
    }
    return merge({}, chartOverrides, zoomOverrides);
  }, [chartOverrides, zoomOverrides]);

  const dataView = dataViewTypes[queryModel?.settings?.chart?.type ?? 'line'];
  const hasRequirements = dataView?.requirements?.(queryModel.query);

  // Run query on mount and whenever the query model is marked dirty.
  useEffect(() => {
    if (!hasRequirements && queryModel.dirty) {
      queryModel.requestRun();
    }
  }, [hasRequirements, queryModel, queryModel.query]);

  if (hasRequirements) {
    return <EmptyState icon="warning-sign" title={hasRequirements.title} description={hasRequirements.description} />;
  }

  const isTimelineIncompatible =
    measurement &&
    queryModel.settings.chart.type === 'timelineView' &&
    !queryModel.query.metrics.some((m) => {
      const metricDetails = measurement.storage?.Metrics?.[m.name];
      return isEnumItem(metricDetails) || getEnumHealthyValue(undefined, m.name) !== undefined;
    });

  const isTableOnly = queryModel.settings.chart.type === 'table';
  const legendEnabled = (isTableOnly || queryModel.settings?.chart?.legend?.enable) ?? true;
  const legendPosition: 'bottom' | 'right' | 'left' = isTableOnly
    ? 'bottom'
    : ((queryModel.settings?.chart?.legend?.position as 'bottom' | 'right' | 'left' | undefined) ?? 'right');
  const directionMap = { right: 'row', left: 'row-reverse', bottom: 'column' } as const;
  const flexDir = directionMap[legendPosition];

  const isAwaitingFirstRun = queryModel.status === 'init' && queryModel.dirty;
  const showSkeleton = isPending || isAwaitingFirstRun || queryModel.isLoading || measurementState.isLoading;
  const showEmpty = !showSkeleton && (!queryModel.hasResults || !measurement || isTimelineIncompatible);
  const queryHealthy = !queryModel.hasError && !measurementState.isError;
  const resultsPlaceholder =
    queryHealthy && emptyState ? emptyState : <EmptyState {...getPlaceholderProps(queryModel, measurementState)} />;

  return (
    <Flex display="grid" width="100%" height="100%" overflow="hidden" gridTemplateRows="1fr" gridTemplateColumns="1fr">
      {showSkeleton && (
        <Flex width="100%" height="100%" bg="cardBackground" gridArea="1 / 1" css={{ zIndex: 2 }}>
          <UdeDataViewSkeleton queryModel={queryModel} legendMaxWidth={legendMaxWidth} padding={padding} />
        </Flex>
      )}
      {showEmpty && (
        <Flex width="100%" height="100%" bg="cardBackground" gridArea="1 / 1" css={{ zIndex: 2 }}>
          {isTimelineIncompatible ? (
            <EmptyState
              icon="timeline-bar-chart"
              title="Timeline view is only available for status-style metrics."
              description="Select a metric like Operational Status, Session State, or Admin Status to use this view."
            />
          ) : (
            resultsPlaceholder
          )}
        </Flex>
      )}
      <Flex flexDirection={flexDir} width="100%" height="100%" overflow="hidden" alignItems="stretch" gridArea="1 / 1">
        {measurement && queryModel.settings.chart.enable && !isTableOnly && (
          <Flex
            ref={containerRef}
            flex={1}
            minHeight={0}
            minWidth={0}
            key={queryModel.mainQueryRequest.requestId}
            overflow="hidden"
            p={padding ?? 1}
            position="relative"
          >
            <Chart
              queryModel={queryModel}
              measurement={measurement}
              onChartRef={onChartRef}
              chartOverrides={mergedOverrides}
            />
            {selection && (
              <>
                <ZoomSelectionOverlay
                  x={selection.rectX}
                  y={selection.rectY}
                  width={selection.rectWidth}
                  height={selection.rectHeight}
                />
                <ZoomSelectionPopover
                  x={selection.popoverX}
                  y={selection.popoverY}
                  onZoom={confirmZoom}
                  onDismiss={dismissSelection}
                />
              </>
            )}
          </Flex>
        )}
        {legendEnabled && (
          <UdeLegend
            queryModel={queryModel}
            position={legendPosition}
            maxWidth={legendMaxWidth}
            onAddRowFilters={onAddRowFilters}
          />
        )}
      </Flex>
    </Flex>
  );
}

export default storeLoader('$metrics')(observer(UdeDataView));
